import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Link as LinkIcon,
  Warning,
  CheckCircle,
  XCircle,
  Clock,
  Upload,
  Plus,
  Phone,
  Envelope,
  Calendar,
  ChatCircle,
  Trash,
  Download,
  File
} from '@phosphor-icons/react';
import { toast } from 'sonner';
import { DocumentViewer } from '../components/DocumentViewer';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const TemporaryAccessPage = () => {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [linkData, setLinkData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  
  // Form state for submission
  const [interactions, setInteractions] = useState([]);
  const [uploadedDocuments, setUploadedDocuments] = useState([]);
  const [notes, setNotes] = useState('');
  const [isInteractionDialogOpen, setIsInteractionDialogOpen] = useState(false);
  const [interactionForm, setInteractionForm] = useState({ type: 'call', notes: '', status_change: '' });
  const [statuses, setStatuses] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [policyNumber, setPolicyNumber] = useState('');
  const [authError, setAuthError] = useState('');
  const [viewingDoc, setViewingDoc] = useState(null);

  useEffect(() => {
    fetchLinkData();
    fetchStatuses();
  }, [token]);

  const fetchStatuses = async () => {
    try {
      const { data } = await axios.get(`${API_URL}/api/statuses`);
      setStatuses(data);
    } catch (e) {
      console.error('Failed to fetch statuses');
    }
  };

  const fetchLinkData = async (policyNum) => {
    try {
      let url = `${API_URL}/api/temporary-links/access/${token}`;
      if (policyNum) url += `?policy_number=${encodeURIComponent(policyNum)}`;
      const { data } = await axios.get(url);
      setLinkData(data);
      setAuthError('');
      
      if (data.status !== 'active' && data.status !== 'auth_required' && data.status !== 'first_submission') {
        setError(data);
      }
    } catch (e) {
      if (e.response?.status === 403 && policyNum) {
        setAuthError('Invalid policy number. Please try again.');
      } else {
        setError({ status: 'error', message: e.response?.data?.detail || 'Failed to access link' });
      }
    } finally {
      setLoading(false);
    }
  };

  const handlePolicyAuth = (e) => {
    e.preventDefault();
    setLoading(true);
    fetchLinkData(policyNumber);
  };

  const handleAddInteraction = () => {
    if (!interactionForm.notes) {
      toast.error('Please enter notes');
      return;
    }
    setInteractions([...interactions, { ...interactionForm, id: Date.now() }]);
    setIsInteractionDialogOpen(false);
    setInteractionForm({ type: 'call', notes: '', status_change: '' });
    toast.success('Interaction added');
  };

  const handleRemoveInteraction = (id) => {
    setInteractions(interactions.filter(i => i.id !== id));
  };

  const handleUploadDocument = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Check if user is logged in
    if (!user) {
      toast.error('Please login to upload documents');
      return;
    }

    setUploading(true);
    const formData = new FormData();
    formData.append('file', file);
    formData.append('record_id', linkData.record.id);

    try {
      const { data } = await axios.post(`${API_URL}/api/documents/upload`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setUploadedDocuments([...uploadedDocuments, data]);
      toast.success('Document uploaded');
      // Reset file input
      e.target.value = '';
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to upload document');
    } finally {
      setUploading(false);
    }
  };

  const handleRemoveUploadedDocument = async (docId) => {
    try {
      await axios.delete(`${API_URL}/api/documents/${docId}`);
      setUploadedDocuments(uploadedDocuments.filter(d => d.id !== docId));
      toast.success('Document removed');
    } catch (e) {
      toast.error('Failed to remove document');
    }
  };

  const handleSubmit = async () => {
    const isExternal = linkData?.link?.is_external;
    if (!isExternal && !user) {
      toast.error('Please login to submit');
      navigate('/login');
      return;
    }

    setSubmitting(true);
    try {
      await axios.post(`${API_URL}/api/temporary-links/${token}/submit`, {
        interactions: interactions.map(({ id, ...rest }) => rest),
        documents: uploadedDocuments.map(d => d.id),
        notes
      });
      toast.success('Submitted successfully! Awaiting approval.');
      fetchLinkData(policyNumber || undefined);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };

  const getInteractionIcon = (type) => {
    switch (type) {
      case 'call': return <Phone size={16} />;
      case 'email': return <Envelope size={16} />;
      case 'meeting': return <Calendar size={16} />;
      default: return <ChatCircle size={16} />;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <div className="text-center">
          <div className="spinner w-8 h-8 mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading...</p>
        </div>
      </div>
    );
  }

  if (linkData?.status === 'auth_required') {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardHeader className="text-center">
            <LinkIcon size={48} className="mx-auto mb-2 text-primary" />
            <CardTitle>Secure Access Link</CardTitle>
            <CardDescription>Please enter the policy number to verify your identity and access this record.</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handlePolicyAuth} className="space-y-4">
              <div className="space-y-2">
                <Label>Policy Number</Label>
                <Input
                  value={policyNumber}
                  onChange={(e) => setPolicyNumber(e.target.value)}
                  placeholder="Enter policy number..."
                  required
                  data-testid="policy-auth-input"
                />
                {authError && <p className="text-sm text-destructive">{authError}</p>}
              </div>
              <Button type="submit" className="w-full" data-testid="policy-auth-submit">
                Access Record
              </Button>
            </form>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-6 text-center">
            {error.status === 'expired' ? (
              <>
                <Clock size={48} className="mx-auto mb-4 text-muted-foreground" />
                <h2 className="text-xl font-bold mb-2">Link Expired</h2>
                <p className="text-muted-foreground">This temporary access link has expired.</p>
              </>
            ) : error.status === 'submitted' ? (
              <>
                <CheckCircle size={48} className="mx-auto mb-4 text-primary" />
                <h2 className="text-xl font-bold mb-2">Already Submitted</h2>
                <p className="text-muted-foreground">This link has been submitted and is pending approval.</p>
              </>
            ) : error.status === 'approved' ? (
              <>
                <CheckCircle size={48} className="mx-auto mb-4 text-green-600" />
                <h2 className="text-xl font-bold mb-2">Completed</h2>
                <p className="text-muted-foreground">This link has served its purpose and is no longer accessible.</p>
              </>
            ) : error.status === 'rejected' ? (
              <>
                <XCircle size={48} className="mx-auto mb-4 text-destructive" />
                <h2 className="text-xl font-bold mb-2">Rejected</h2>
                <p className="text-muted-foreground">The submission for this link was rejected.</p>
              </>
            ) : (
              <>
                <Warning size={48} className="mx-auto mb-4 text-destructive" />
                <h2 className="text-xl font-bold mb-2">Access Denied</h2>
                <p className="text-muted-foreground">{error.message}</p>
              </>
            )}
            <Button className="mt-6" onClick={() => navigate('/')}>
              Go to Dashboard
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  // First submission summary view
  if (linkData?.status === 'first_submission') {
    const summary = linkData.summary || {};
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-4">
        <Card className="max-w-lg w-full" data-testid="submission-summary-card">
          <CardContent className="pt-6">
            <div className="text-center mb-6">
              <CheckCircle size={56} className="mx-auto mb-3 text-green-600" />
              <h2 className="text-xl font-bold mb-1">Submission Received</h2>
              <p className="text-muted-foreground text-sm">{linkData.message}</p>
            </div>
            <div className="space-y-3 border rounded-lg p-4 bg-secondary/30">
              <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wider">Submission Summary</h3>
              {summary.customer_name && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Customer</span>
                  <span className="font-medium" data-testid="summary-customer">{summary.customer_name}</span>
                </div>
              )}
              {summary.policy_number && (
                <div className="flex justify-between text-sm">
                  <span className="text-muted-foreground">Policy Number</span>
                  <span className="font-medium" data-testid="summary-policy">{summary.policy_number}</span>
                </div>
              )}
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Documents Submitted</span>
                <span className="font-medium" data-testid="summary-docs">{summary.documents_count || 0}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Interactions Added</span>
                <span className="font-medium" data-testid="summary-interactions">{summary.interactions_count || 0}</span>
              </div>
              {summary.notes && (
                <div className="pt-2 border-t">
                  <span className="text-xs text-muted-foreground">Notes</span>
                  <p className="text-sm mt-1" data-testid="summary-notes">{summary.notes}</p>
                </div>
              )}
              {summary.submitted_at && (
                <div className="flex justify-between text-sm pt-2 border-t">
                  <span className="text-muted-foreground">Submitted At</span>
                  <span className="font-medium">{new Date(summary.submitted_at).toLocaleString()}</span>
                </div>
              )}
            </div>
            <Button className="mt-6 w-full" onClick={() => navigate('/')} data-testid="summary-home-btn">
              Done
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const { record, link, interactions: existingInteractions, documents } = linkData;

  return (
    <div className="min-h-screen bg-background p-4 md:p-8">
      <div className="max-w-4xl mx-auto space-y-6 animate-fade-in">
        {/* Header */}
        <Card className="border-primary/20 bg-primary/5">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center">
                <LinkIcon size={24} className="text-primary" />
              </div>
              <div className="flex-1">
                <h1 className="text-xl font-bold">Temporary Access</h1>
                <p className="text-muted-foreground text-sm">
                  Shared by {link.created_by_name} • Expires {new Date(link.expires_at).toLocaleString()}
                </p>
                {!user ? (
                  <Badge variant="outline" className="mt-2">
                    <Warning size={14} className="mr-1" />
                    Login required to submit
                  </Badge>
                ) : (
                  <Badge variant="default" className="mt-2">
                    Logged in as {user.name}
                  </Badge>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Record Details */}
        <Card>
          <CardHeader>
            <CardTitle className="text-lg">Record Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {Object.entries(record.data || {}).map(([key, value]) => (
                <div key={key} className="space-y-1">
                  <Label className="text-xs text-muted-foreground">{key}</Label>
                  <p className="text-sm font-medium">{String(value) || '-'}</p>
                </div>
              ))}
            </div>
            <div className="flex items-center gap-2 mt-4 pt-4 border-t">
              <span className="text-sm text-muted-foreground">Current Status:</span>
              <Badge variant="outline">{record.status}</Badge>
            </div>
          </CardContent>
        </Card>

        {/* Existing Interactions */}
        {existingInteractions?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Previous Interactions</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {existingInteractions.map(interaction => (
                  <div key={interaction.id} className="flex items-start gap-3 p-3 bg-secondary/50 rounded-md">
                    <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center">
                      {getInteractionIcon(interaction.type)}
                    </div>
                    <div>
                      <div className="flex items-center gap-2">
                        <Badge variant="outline" className="text-xs capitalize">{interaction.type}</Badge>
                        <span className="text-xs text-muted-foreground">
                          {new Date(interaction.created_at).toLocaleString()}
                        </span>
                      </div>
                      <p className="text-sm mt-1">{interaction.notes}</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Existing Documents */}
        {documents?.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Existing Documents</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                {documents.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-secondary/50 rounded-md">
                    <div className="flex items-center gap-2">
                      <File size={18} className="text-muted-foreground" />
                      <span className="text-sm">{doc.original_filename}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge variant="outline" className="text-xs">
                        {(doc.size / 1024).toFixed(1)} KB
                      </Badge>
                      <Button variant="ghost" size="sm" onClick={() => setViewingDoc(doc)} data-testid={`view-doc-${doc.id}`}>
                        View
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* New Interactions */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Your Updates</CardTitle>
              <CardDescription>Add interactions and notes for this record</CardDescription>
            </div>
            <Button size="sm" onClick={() => setIsInteractionDialogOpen(true)} data-testid="add-temp-interaction-btn">
              <Plus size={16} className="mr-1" /> Add Interaction
            </Button>
          </CardHeader>
          <CardContent className="space-y-4">
            {interactions.length > 0 && (
              <div className="space-y-2">
                {interactions.map(interaction => (
                  <div key={interaction.id} className="flex items-start justify-between p-3 bg-secondary/50 rounded-md">
                    <div className="flex items-start gap-3">
                      <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                        {getInteractionIcon(interaction.type)}
                      </div>
                      <div>
                        <div className="flex items-center gap-2">
                          <Badge variant="outline" className="text-xs capitalize">{interaction.type}</Badge>
                          {interaction.status_change && (
                            <Badge variant="secondary" className="text-xs">
                              → {interaction.status_change}
                            </Badge>
                          )}
                        </div>
                        <p className="text-sm mt-1">{interaction.notes}</p>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleRemoveInteraction(interaction.id)}
                      className="text-destructive"
                    >
                      Remove
                    </Button>
                  </div>
                ))}
              </div>
            )}
            
            <div className="space-y-2">
              <Label>Additional Notes</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any additional notes or summary..."
                data-testid="temp-notes"
              />
            </div>
          </CardContent>
        </Card>

        {/* Document Upload Section */}
        <Card>
          <CardHeader className="flex flex-row items-center justify-between">
            <div>
              <CardTitle className="text-lg">Upload Documents</CardTitle>
              <CardDescription>Upload any collected documents for this record</CardDescription>
            </div>
            {user && (
              <Label htmlFor="temp-doc-upload" className="cursor-pointer">
                <Button size="sm" asChild disabled={uploading}>
                  <span>
                    <Upload size={16} className="mr-1" /> 
                    {uploading ? 'Uploading...' : 'Upload'}
                  </span>
                </Button>
                <Input
                  id="temp-doc-upload"
                  type="file"
                  className="hidden"
                  onChange={handleUploadDocument}
                  disabled={uploading}
                  data-testid="temp-doc-upload"
                />
              </Label>
            )}
          </CardHeader>
          <CardContent>
            {!user ? (
              <div className="text-center py-6 text-muted-foreground">
                <Upload size={32} className="mx-auto mb-2 opacity-50" />
                <p>Please login to upload documents</p>
              </div>
            ) : uploadedDocuments.length > 0 ? (
              <div className="space-y-2">
                {uploadedDocuments.map(doc => (
                  <div key={doc.id} className="flex items-center justify-between p-3 bg-green-50 rounded-md">
                    <div className="flex items-center gap-2">
                      <File size={18} className="text-green-600" />
                      <span className="text-sm font-medium">{doc.original_filename}</span>
                      <Badge variant="outline" className="text-xs">New</Badge>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleRemoveUploadedDocument(doc.id)}
                      className="text-destructive h-8 w-8"
                    >
                      <Trash size={14} />
                    </Button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-6 text-muted-foreground">
                <Upload size={32} className="mx-auto mb-2 opacity-50" />
                <p>No documents uploaded yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Submit */}
        <div className="flex justify-end gap-2">
          <Button
            onClick={handleSubmit}
            disabled={submitting || !user}
            className="min-w-32"
            data-testid="submit-temp-link-btn"
          >
            {submitting ? (
              <>
                <span className="spinner w-4 h-4 mr-2"></span>
                Submitting...
              </>
            ) : (
              'Submit for Approval'
            )}
          </Button>
        </div>

        {/* Add Interaction Dialog */}
        <Dialog open={isInteractionDialogOpen} onOpenChange={setIsInteractionDialogOpen}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Add Interaction</DialogTitle>
              <DialogDescription>Log an interaction with this customer</DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label>Type</Label>
                  <Select value={interactionForm.type} onValueChange={(v) => setInteractionForm({...interactionForm, type: v})}>
                    <SelectTrigger data-testid="temp-interaction-type">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="call">Call</SelectItem>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="meeting">Meeting</SelectItem>
                      <SelectItem value="note">Note</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Change Status (Optional)</Label>
                  <Select value={interactionForm.status_change || "nochange"} onValueChange={(v) => setInteractionForm({...interactionForm, status_change: v === "nochange" ? "" : v})}>
                    <SelectTrigger data-testid="temp-interaction-status">
                      <SelectValue placeholder="No change" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="nochange">No change</SelectItem>
                      {statuses.map(s => (
                        <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Notes</Label>
                <Textarea
                  value={interactionForm.notes}
                  onChange={(e) => setInteractionForm({...interactionForm, notes: e.target.value})}
                  placeholder="Describe the interaction..."
                  data-testid="temp-interaction-notes"
                />
              </div>
              <div className="flex justify-end gap-2 pt-4">
                <Button variant="outline" onClick={() => setIsInteractionDialogOpen(false)}>Cancel</Button>
                <Button onClick={handleAddInteraction} data-testid="confirm-temp-interaction">Add</Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
        <DocumentViewer document={viewingDoc} open={!!viewingDoc} onClose={() => setViewingDoc(null)} />
      </div>
    </div>
  );
};

export default TemporaryAccessPage;
